import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Card, Typography, Button, Select, MenuItem } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { callFrequency, callTime, register } from "service/axiosConfig";
import { toast } from "react-toastify";
import { setEncodedUserProfile } from "../../../utils/authHelpers";
import Checkbox from "@mui/material/Checkbox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const timezones = [
  { label: "Eastern Time (US & Canada)", id: "America/New_York" },
  { label: "Central Time (US & Canada)", id: "America/Chicago" },
  { label: "Mountain Time (US & Canada)", id: "America/Denver" },
  { label: "Arizona Time (No DST)", id: "America/Phoenix" },
  { label: "Pacific Time (US & Canada)", id: "America/Los_Angeles" },
  { label: "Alaska Time", id: "America/Anchorage" },
  { label: "Hawaii Time", id: "Pacific/Honolulu" },
];

function Cover() {
  const navigate = useNavigate();
  const [preSelectedPlan, setPreSelectedPlan] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("selectedPlan");

    if (plan) {
      setPreSelectedPlan(plan);
      localStorage.setItem("preSelectedPlan", plan); // store for dashboard later
    }
  }, []);
  function getDefaultTimezone() {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("Browser Timezone:", browserTz);
    const match = timezones.find((tz) => tz.id === browserTz);
    return match ? match.id : "America/Los_Angeles"; // fallback to Pacific
  }
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    relationship: "",
    checkInName: "",
    theirPhone: "",
    callTime: "21", // default 10 AM - 10:30 AM
    callDay: "1", // default Monday/Wednesday/Friday days
    timeZone: getDefaultTimezone(),
    consentToSms: false,
  });

  const [frequency, setFrequency] = useState([]);
  const [time, setTime] = useState([]);
  const [timeZone] = useState(timezones);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const res = await callFrequency();
  //     const response = await callTime();
  //     if (response) setTime(response);
  //     if (res) {
  //       const filtered = res.filter((item) => item.id !== 4);
  //       setFrequency(filtered);
  //     }
  //   };
  //   fetchData();
  // }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRegister = async () => {
    const errors = [];
    if (!formData.name?.trim()) errors.push("Name is required.");
    if (!formData.email?.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      errors.push("Valid email is required.");
    if (!isValidPhoneNumber(formData.phone)) {
      errors.push("Your phone number is invalid.");
    }
    if (!formData.relationship?.trim()) errors.push("Relationship is required.");
    if (!formData.checkInName?.trim()) errors.push("Check-in name is required.");
    if (!isValidPhoneNumber(formData.theirPhone)) {
      errors.push("Their phone number is invalid.");
    }
    if (!formData.callTime) errors.push("Call Time is required.");
    if (!formData.callDay) errors.push("Call Frequency is required.");
    if (!formData.timeZone) errors.push("Time Zone is required.");
    if (!formData.consentToSms) {
      errors.push("Please agree to receive text messages from MyHeartline.");
    }

    if (errors.length > 0) {
      errors.forEach((msg) => toast.error(msg));
      return;
    }

    const response = await register(formData);
    const profile = {
      name: response.data.data.myName,
      email: response.data.data.myEmail,
      phoneNumber: response.data.data.myMobilePhone,
      emailOtpVerification: response.data.data.isEmailVerified,
      phoneOtpVerification: response.data.data.isPhoneVerified,
      magicToken: response.data.data.magicToken,
    };

    setEncodedUserProfile(profile);
    localStorage.setItem("_s44", true);
    navigate("/verification");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(to bottom right, #fff7f0, #ffffff, #f0fff7)",
        fontFamily:
          'var(--default-font-family, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Heartline clickable logo */}
          <Box
            onClick={() => (window.location.href = "https://myheartline.com")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                bgcolor: "#7e2a0c",
                borderRadius: "12px",
              }}
            >
              {/* Client’s Heart SVG */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  width: "20px",
                  height: "20px",
                  display: "block",
                  verticalAlign: "middle",
                }}
              >
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            </Box>
            <Typography
              fontWeight="bold"
              sx={{
                fontFamily: "inherit",
                fontSize: "20px",
                color: "#0f172a",
              }}
            >
              Heartline
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Hero */}
      <Box textAlign="center" mt={4} mb={6} px={{ xs: 2, sm: 4 }}>
        <Typography
          variant="h1"
          fontWeight="bold"
          fontFamily="inherit"
          sx={{ fontSize: "36px", color: "#0f172a", mb: 1 }}
        >
          Start Your Free Trial
        </Typography>
        <Typography
          fontFamily="inherit"
          sx={{
            mx: "auto",
            fontSize: "20px",
            color: "#475569",
          }}
        >
          Join thousands of families who trust Heartline to keep their loved ones safe and
          connected.
        </Typography>
      </Box>
      {/* Main Layout (two columns) */}
      <Box
        display="flex"
        justifyContent="center"
        gap={6}
        px={{ xs: 2, md: 4 }}
        mb={6}
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "center", md: "flex-start" }}
      >
        {/* Left Column: Registration Form */}
        <Box flex={1} maxWidth={480}>
          {/* Form */}
          <Box maxWidth={480} mx="auto" px={2} mb={6}>
            <Card
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: "12px",
                borderColor: "#fcd9bd",
                border: "2px solid #fcd9bd",
                boxShadow: "none",
                fontFamily: "inherit",
              }}
            >
              <Typography
                variant="h4"
                fontWeight={400}
                fontSize={"24px"}
                textAlign="center"
                sx={{ mb: 1, fontFamily: "inherit", color: "#0f172a" }}
              >
                Create Your Account
              </Typography>
              <Typography
                fontSize="16px"
                textAlign="center"
                sx={{ mb: 3, color: "#475569", fontFamily: "inherit" }}
              >
                Get started with your 7-day free trial
              </Typography>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleRegister();
                }}
              >
                {[
                  { id: "name", label: "Name", placeholder: "Enter your full name" },
                  { id: "email", label: "Email Address", placeholder: "Enter your email address" },
                  {
                    id: "phone",
                    label: "Mobile Phone #",
                    placeholder: "Enter your mobile phone number",
                    type: "phone",
                  },
                  {
                    id: "checkInName",
                    label: "Care Recipient Name",
                    placeholder: "Enter care recipient's name",
                  },
                  {
                    id: "theirPhone",
                    label: "Care Recipient Phone #",
                    placeholder: "Enter care recipient's phone",
                    type: "phone",
                  },
                ].map((field) => (
                  <Box key={field.id} mb={2} textAlign="left">
                    <Typography
                      component="label"
                      htmlFor={field.id}
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#0f172a",
                        fontFamily: "inherit",
                      }}
                    >
                      {field.label}
                    </Typography>

                    {field.type === "phone" ? (
                      <Box
                        sx={{
                          "& .PhoneInputInput": {
                            border: "none",
                            outline: "none",
                            boxShadow: "none",
                            background: "#f8fafc",
                            fontSize: "0.9rem",
                            fontFamily: "inherit",
                            padding: "10px 12px",
                            borderRadius: "12px",
                            width: "100%",
                            boxSizing: "border-box",
                            color: "#0f172a",
                            height: "16px",
                          },
                          "& .PhoneInput--focus": {
                            borderColor: "#7c2d12",
                            boxShadow: "0 0 0 1px #7c2d12",
                          },
                        }}
                      >
                        <PhoneInput
                          id={field.id}
                          value={formData[field.id]}
                          onChange={(v) => handleChange(field.id, v)}
                          defaultCountry="US"
                          countries={["US"]}
                          disableArrowIcon
                          disableCountryPicker
                          international={false}
                          addInternationalOption={false}
                          placeholder={field.placeholder}
                          style={{
                            width: "100%",
                            fontSize: "0.9rem",
                            fontFamily: "inherit",
                            padding: "10px 12px",
                            backgroundColor: "#f8fafc",
                            border: "0px solid #e2e8f0",
                            borderRadius: "12px",
                            outline: "none",
                            boxSizing: "border-box",
                          }}
                        />
                      </Box>
                    ) : (
                      <input
                        id={field.id}
                        value={formData[field.id]}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        style={{
                          width: "100%",
                          fontSize: "0.9rem",
                          fontFamily: "inherit",
                          padding: "10px 12px",
                          backgroundColor: "#f8fafc",
                          border: "0px solid #e2e8f0",
                          borderRadius: "12px",
                          outline: "none",
                          marginTop: "4px",
                          transition: "border-color 0.2s, box-shadow 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#7c2d12";
                          e.target.style.boxShadow = "0 0 0 1px #7c2d12";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#e2e8f0";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    )}
                  </Box>
                ))}

                {/* Relationship */}
                <Box mb={2} textAlign="left">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0f172a",
                      fontFamily: "inherit",
                    }}
                  >
                    Relationship
                  </Typography>
                  <Select
                    value={formData.relationship || ""}
                    onChange={(e) => handleChange("relationship", e.target.value)}
                    displayEmpty
                    IconComponent={ArrowDropDownIcon}
                    fullWidth
                    sx={{
                      width: "100%",
                      fontSize: "0.9rem",
                      fontFamily: "inherit",
                      padding: "10px 12px",
                      backgroundColor: "#f8fafc",
                      border: "0px solid #e2e8f0",
                      borderRadius: "12px",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                      borderRadius: "12px",
                      mt: 0.5,
                      "& .MuiOutlinedInput-notchedOutline": { border: "0px solid #e2e8f0" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7c2d12" },
                    }}
                  >
                    <MenuItem value="" disabled>
                      This is my...
                    </MenuItem>
                    {["Spouse", "Sibling", "Child", "Parent", "Friend", "Patient", "Other"].map(
                      (relation) => (
                        <MenuItem key={relation} value={relation}>
                          {relation}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </Box>

                {/* Call Time + Frequency 
                <Box display="flex" gap={2} mb={2}>
                  <Box flex={1} sx={{ maxWidth: 182 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#0f172a",
                        fontFamily: "inherit",
                      }}
                    >
                      Call Time
                    </Typography>
                    <Select
                      value={formData.callTime || ""}
                      onChange={(e) => handleChange("callTime", e.target.value)}
                      displayEmpty
                      IconComponent={ArrowDropDownIcon}
                      fullWidth
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 3.5, // 5 items (5.5 for spacing)
                            width: 180, // optional fixed width
                          },
                        },
                      }}
                      sx={{
                        width: "100%",
                        fontSize: "0.9rem",
                        fontFamily: "inherit",
                        padding: "10px 12px",
                        backgroundColor: "#f8fafc",
                        border: "0px solid #e2e8f0",
                        borderRadius: "12px",
                        outline: "none",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                        borderRadius: "12px",
                        mt: 0.5,
                        "& .MuiOutlinedInput-notchedOutline": { border: "0px solid #e2e8f0" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7c2d12",
                        },
                      }}
                    >
                      <MenuItem
                        value=""
                        disabled
                        sx={{
                          fontSize: "12px",
                          fontFamily: "inherit",
                          justifyContent: "flex-start",
                          px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                          py: 0.5, // trim top & bottom padding (optional)
                          minHeight: "unset", // remove default tall height
                          lineHeight: 1.4, // tighter line spacing
                        }}
                      >
                        Preferred time
                      </MenuItem>
                      {time.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                          sx={{
                            fontSize: "12px",
                            fontFamily: "inherit",
                            justifyContent: "flex-start",
                            px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                            py: 0.5, // trim top & bottom padding (optional)
                            minHeight: "unset", // remove default tall height
                            lineHeight: 1.4, // tighter line spacing
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  <Box flex={1} sx={{ maxWidth: 182 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#0f172a",
                        fontFamily: "inherit",
                      }}
                    >
                      Call Frequency
                    </Typography>
                    <Select
                      value={formData.callDay || ""}
                      onChange={(e) => handleChange("callDay", e.target.value)}
                      displayEmpty
                      IconComponent={ArrowDropDownIcon}
                      fullWidth
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 3.5, // 5 items (5.5 for spacing)
                            width: 180, // optional fixed width
                          },
                        },
                      }}
                      sx={{
                        width: "100%",
                        fontSize: "0.9rem",
                        fontFamily: "inherit",
                        padding: "10px 12px",
                        backgroundColor: "#f8fafc",
                        border: "0px solid #e2e8f0",
                        borderRadius: "12px",
                        outline: "none",
                        boxSizing: "border-box",
                        borderRadius: "12px",
                        mt: 0.5,
                        "& .MuiOutlinedInput-notchedOutline": { border: "0px solid #e2e8f0" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#7c2d12",
                        },
                      }}
                    >
                      <MenuItem
                        value=""
                        disabled
                        sx={{
                          fontSize: "12px",
                          fontFamily: "inherit",
                          justifyContent: "flex-start",
                          px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                          py: 0.5, // trim top & bottom padding (optional)
                          minHeight: "unset", // remove default tall height
                          lineHeight: 1.4, // tighter line spacing
                        }}
                      >
                        How often
                      </MenuItem>
                      {frequency.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                          sx={{
                            fontSize: "12px",
                            fontFamily: "inherit",
                            justifyContent: "flex-start",
                            px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                            py: 0.5, // trim top & bottom padding (optional)
                            minHeight: "unset", // remove default tall height
                            lineHeight: 1.4, // tighter line spacing
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>*/}

                {/* Time Zone 
                <Box mb={2} textAlign="left">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0f172a",
                      fontFamily: "inherit",
                    }}
                  >
                    Time Zone
                  </Typography>
                  <Select
                    value={formData.timeZone || ""}
                    onChange={(e) => handleChange("timeZone", e.target.value)}
                    displayEmpty
                    IconComponent={ArrowDropDownIcon}
                    fullWidth
                    sx={{
                      width: "100%",
                      fontSize: "0.9rem",
                      fontFamily: "inherit",
                      padding: "10px 12px",
                      backgroundColor: "#f8fafc",
                      border: "0px solid #e2e8f0",
                      borderRadius: "12px",
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                      borderRadius: "12px",
                      mt: 0.5,
                      "& .MuiOutlinedInput-notchedOutline": { border: "0px solid #e2e8f0" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#7c2d12" },
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                      sx={{
                        fontSize: "14px",
                        fontFamily: "inherit",
                        justifyContent: "flex-start",
                        px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                        py: 0.5, // trim top & bottom padding (optional)
                        minHeight: "unset", // remove default tall height
                        lineHeight: 1.4, // tighter line spacing
                      }}
                    >
                      Select time zone
                    </MenuItem>
                    {timezones.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                        sx={{
                          fontSize: "14px",
                          fontFamily: "inherit",
                          justifyContent: "flex-start",
                          px: 0.5, // trim left & right padding (default is 16px → use 4px/8px)
                          py: 0.5, // trim top & bottom padding (optional)
                          minHeight: "unset", // remove default tall height
                          lineHeight: 1.4, // tighter line spacing
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>*/}

                {/* Submit */}
                <Button
                  type="submit"
                  fullWidth
                  sx={{
                    bgcolor: "#7c2d12",
                    "&:hover": { bgcolor: "#65200d", color: "#fff" },
                    fontWeight: 600,
                    fontSize: "14px",
                    borderRadius: "12px",
                    fontFamily: "inherit",
                    textTransform: "none",
                    color: "#fff",
                    py: 1.2,
                  }}
                >
                  Start Free Trial
                </Button>
                {/* Consent */}
                <Box
                  mt={3}
                  mb={3}
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#475569",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                    display: "flex",
                    justifyContent: "center",
                    // alignItems: "flex-start",
                    gap: 2,
                    flexDirection: "column",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" flex={1}>
                    <Checkbox
                      checked={formData.consentToSms}
                      onChange={(e) => handleChange("consentToSms", e.target.checked)}
                      disableRipple
                      sx={{
                        scale: 0.8,
                        width: 20,
                        height: 20,
                        mr: 1,
                        mt: "2px",
                        borderRadius: "4px",
                        "& .MuiSvgIcon-root": {
                          fontSize: 16,
                          backgroundColor: "#f8fafc", // ✅ background inside border
                        },
                        "&.Mui-checked .MuiSvgIcon-root": {
                          color: "#7c2d12", // brand color when checked
                          borderColor: "#7c2d12",
                          border: "0px solid #7c2d12",
                          scale: 1.2,
                        },
                        "&:hover .MuiSvgIcon-root": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    />

                    <Typography
                      fontWeight={500}
                      fontFamily="inherit"
                      sx={{ fontSize: "14px", color: "#475569" }}
                    >
                      By providing your phone number, you agree to receive text messages from
                      MyHeartline. Message and data rates may apply. Message frequency varies. Reply
                      STOP to opt out of messages.
                    </Typography>
                  </Box>

                  {/* Right side: Privacy Policy */}
                  <Link
                    to="https://myheartline.com/privacy"
                    target="_blank"
                    style={{
                      color: "#7c2d12",
                      fontWeight: 600,
                      fontSize: "16px",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Box>

                {/* Already Registered */}
                <Typography textAlign="center" mt={2} fontSize="14px" fontFamily="inherit">
                  Already Registered?{" "}
                  <Link
                    to="/authentication/sign-in"
                    style={{
                      color: "#7c2d12",
                      fontWeight: 600,
                      fontSize: "16px",
                      textDecoration: "none",
                    }}
                  >
                    Login
                  </Link>
                </Typography>
              </form>
            </Card>
          </Box>
        </Box>
        {/* Right Column: What Happens Next */}
        <Box flex={1} maxWidth={400}>
          <Typography
            variant="h3"
            fontWeight={600}
            mb={2}
            fontFamily="inherit"
            color="#0f172a"
            fontSize={"24px"}
          >
            What Happens Next:
          </Typography>

          {[
            {
              title: "Instant Setup",
              desc: "We’ll email you a magic link to access your dashboard and settings.",
            },
            {
              title: "First Check-In Call",
              desc: "Your loved one receives their first wellness call within 24 hours.",
            },
            {
              title: "Daily Email Reports",
              desc: "Receive detailed summaries delivered to your inbox after each call.",
            },
            {
              title: "Smart Health Monitoring",
              desc: "Track wellness trends and receive intelligent alerts for changes.",
            },
          ].map((step, i) => (
            <Box key={i} display="flex" alignItems="flex-start" mb={2}>
              {/* Custom Green Circle with Check Icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                  bgcolor: "#dcfce7", // Tailwind's green-100
                  borderRadius: "50%",
                  mt: "2px",
                  mr: 1.5,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ width: "16px", height: "16px", color: "#16a34a" }} // Tailwind's green-600
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </Box>

              {/* Text content */}
              <Box>
                <Typography fontWeight={600} fontFamily="inherit" sx={{ fontSize: "16px" }}>
                  {step.title}
                </Typography>
                <Typography fontFamily="inherit" sx={{ fontSize: "14px", color: "#475569" }}>
                  {step.desc}
                </Typography>
              </Box>
            </Box>
          ))}

          {/* Trusted by families */}
          <Card
            variant="outlined"
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "12px",
              borderColor: "#e2e8f0",
              fontFamily: "inherit",
            }}
          >
            <Typography
              textAlign={"center"}
              fontWeight={500}
              variant="h4"
              fontFamily="inherit"
              sx={{ fontSize: "16px", color: "#000000ff", mb: 2 }}
            >
              Trusted by families nationwide
            </Typography>
            <Box display="flex" justifyContent={"center"} gap={3}>
              {/* Families */}
              {/* <Box textAlign="center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#16a34a", margin: "0 auto 8px" }} // green-600
                >
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <Typography fontWeight={600} fontFamily="inherit" fontSize="14px">
                  10,000+
                </Typography>
                <Typography fontFamily="inherit" sx={{ fontSize: "12px", color: "#475569" }}>
                  Families
                </Typography>
              </Box> */}

              {/* Secure */}
              <Box textAlign="center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#7c2d12", margin: "0 auto 8px" }} // Heartline brown
                >
                  <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                </svg>
                <Typography fontWeight={600} fontFamily="inherit" fontSize="14px">
                  Secure
                </Typography>
                <Typography fontFamily="inherit" sx={{ fontSize: "12px", color: "#475569" }}>
                  & Private
                </Typography>
              </Box>

              {/* 5-Star */}
              <Box textAlign="center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#7c2d12", margin: "0 auto 8px" }}
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
                <Typography fontWeight={600} fontFamily="inherit" fontSize="14px">
                  5-Star
                </Typography>
                <Typography fontFamily="inherit" sx={{ fontSize: "12px", color: "#475569" }}>
                  Rated
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
      {/* Divider line */}
      <Box
        sx={{
          borderTop: "1px solid #e2e8f0",
          maxWidth: { xs: 400, md: 900 },
          width: "100%",
          px: { xs: 2, md: 0 },
          mx: "auto",
          mt: -6,
        }}
      />

      {/* Disclaimer Section */}
      <Box
        sx={{
          px: 4,
          py: 4,
          display: "flex",
          justifyContent: "center",
          fontFamily: "inherit",
        }}
      >
        <Card
          sx={{
            maxWidth: 880,
            width: "100%",
            p: 3,
            bgcolor: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "none",
          }}
        >
          <Typography fontFamily="inherit" sx={{ fontSize: "16px", color: "#334155" }}>
            <strong>Disclaimer:</strong> Heartline is not a healthcare provider and does not offer
            medical advice, diagnosis, or treatment. It is a smart wellness check-in platform
            designed to capture and summarize user-reported information. All data is shared
            voluntarily and is not protected under HIPAA. Heartline and its agents are not liable
            for any health outcomes or inaccuracies. Always consult a qualified medical professional
            regarding any health-related concerns.
          </Typography>
          <Typography
            component={Link}
            to="https://myheartline.com/terms"
            target="_blank"
            sx={{
              fontFamily: "inherit",
              fontSize: "16px",
              fontWeight: 600,
              mt: 1,
              display: "inline-block",
              color: "#7c2d12",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Terms of Use
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}

export default Cover;
